import React, { useRef, useState } from 'react';
import ConfirmationModal from '../../design-system/components/atoms/confirmation-modal/confirmation-modal';
import { useShouldRouteDataExportToOwner } from '../../utils/data-export-permission';

const EXPORT_CONFIRMATION_TITLE = 'Export will be shared with your admin';
const EXPORT_CONFIRMATION_CONTENT =
  'Based on your account settings, the exported report will be emailed to your account admin. Do you want to continue?';

type UseExportConfirmation = {
  guardExport: <T>(exportAction: () => T) => T | void;
  /** True while the route-to-owner confirmation dialog is open. */
  isConfirmationOpen: boolean;
  /** Render this once inside the component tree. */
  exportConfirmationModal: React.ReactNode;
};

export const useExportConfirmation = (): UseExportConfirmation => {
  const shouldRouteToOwner = useShouldRouteDataExportToOwner();
  const [show, setShow] = useState(false);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const pendingExportRef = useRef<(() => unknown) | null>(null);

  function guardExport<T>(exportAction: () => T): T | void {
    if (!shouldRouteToOwner) {
      return exportAction();
    }

    if (show || isRequestPending) {
      return undefined;
    }

    pendingExportRef.current = exportAction;
    setShow(true);
    return undefined;
  }

  const handleClose = () => {
    if (isRequestPending) {
      return;
    }

    pendingExportRef.current = null;
    setShow(false);
  };

  const handleConfirm = async () => {
    const exportAction = pendingExportRef.current;

    if (!exportAction) {
      setShow(false);
      return;
    }

    pendingExportRef.current = null;
    setIsRequestPending(true);

    try {
      await Promise.resolve(exportAction());
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsRequestPending(false);
      setShow(false);
    }
  };

  const exportConfirmationModal = (
    <ConfirmationModal
      show={show}
      onClose={handleClose}
      onSubmit={handleConfirm}
      isRequestPending={isRequestPending}
      title={EXPORT_CONFIRMATION_TITLE}
      content={EXPORT_CONFIRMATION_CONTENT}
      iconType="info"
      showSuccessButton
      showCancelButton
      successButtonText="Continue"
      cancelButtonText="Cancel"
      showCloseButton
    />
  );

  return {
    guardExport,
    isConfirmationOpen: show || isRequestPending,
    exportConfirmationModal,
  };
};
